import React from "react";
import { useDroppable } from "@dnd-kit/core";
import Typography from "../../../common/Typography";
import Button from "../../../common/Button";
import { CheckCircle, XCircle, X } from "lucide-react";

interface CategoryDropZoneProps {
  id: string;
  title: string;
  items: string[];
  isCorrect: boolean;
  isIncorrect: boolean;
  isLocked: boolean;
  isDisabled: boolean;
  // eslint-disable-next-line no-unused-vars
  onMoveToAvailable?: (itemId: string) => void;
  itemTexts: { [itemId: string]: string };
  // eslint-disable-next-line no-unused-vars
  getItemCorrectness?: (itemId: string) => boolean | null;
}

const CategoryDropZone: React.FC<CategoryDropZoneProps> = ({
  id,
  items,
  isCorrect,
  isIncorrect,
  isLocked,
  isDisabled,
  onMoveToAvailable,
  itemTexts,
  getItemCorrectness,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: isDisabled,
  });

  const getBorderColor = () => {
    if (isCorrect) {return "border-green-300 bg-green-50 shadow-green-100";}
    if (isIncorrect) {return "border-red-300 bg-red-50 shadow-red-100";}
    if (isDisabled) {return "border-gray-200 bg-gray-100 opacity-50";}
    if (items.length > 0) {return "border-solid bg-white shadow-sm";}
    return "border-gray-200 bg-gray-50 shadow-gray-100";
  };

  return (
    <div
      ref={setNodeRef}
      data-testid="dnd-category-drop-zone"
      data-dnd-id={id}
      className={`min-h-[120px] p-4 border-2 border-dashed rounded-lg transition-all duration-200 mb-4 ${
        isDisabled
          ? getBorderColor()
          : isOver
            ? "border-blue-400 bg-blue-50"
            : getBorderColor()
      }`}
    >
      <div className="min-h-[60px]">
        {items?.length > 0 ? (
          <div className="space-y-2">
            {items?.map((itemId, index) => {
              const itemCorrectness = getItemCorrectness?.(itemId);
              const isItemCorrect = itemCorrectness === true;
              const isItemIncorrect = itemCorrectness === false;

              const getItemBorderColor = () => {
                if (isItemCorrect)
                {return "border-green-300 bg-green-50 shadow-green-100";}
                if (isItemIncorrect)
                {return "border-red-300 bg-red-50 shadow-red-100";}
                return "border-gray-200 bg-gray-50 shadow-gray-100";
              };

              return (
                <div key={`${itemId}-${index}`} className="relative">
                  <div
                    className={`px-4 py-4 rounded-xl border-2 border-dashed mb-4 transition-all duration-200 ${getItemBorderColor()}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Typography
                          variant="h6"
                          weight="extrabold"
                          className="pl-2"
                        >
                          {itemTexts[itemId] || itemId}
                        </Typography>
                        {isItemCorrect && (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        )}
                        {isItemIncorrect && (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        )}
                      </div>
                      {/* Move to available button (only show before checking and for wrong items) */}
                      {!isLocked &&
                        onMoveToAvailable &&
                        itemCorrectness !== true && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => onMoveToAvailable(itemId)}
                          className="p-1 text-[#FF6F86] hover:text-[#CA3357] hover:bg-[#FFE1E6] rounded-full mr-1"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Typography variant="p" className="text-gray-500">
            Drop items here
          </Typography>
        )}
      </div>
    </div>
  );
};

export default CategoryDropZone;
